import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { stack } from "@ui/style";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useMemo, useState } from "react";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { InviteSurvey } from "@ui/pages/home/components/survey/invite-survey";
import { CreateSurveyForm } from "@ui/pages/home/components/survey/create-survey-form";
import { analyticsService, translationsManager, userSurveyManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { Survey } from "@domain/user/user-survey";
import { useObservable } from "micro-observables";

export enum SurveyDialogStep {
	SurveyInvitation = "SurveyInvitation",
	SurveyForm = "SurveyForm",
}

export interface AddSurveyDialogProps extends DialogProps {
	step?: SurveyDialogStep;
	survey: Survey;
}

export const AddSurveyDialog: React.FC<AddSurveyDialogProps> = ({ survey, onHide, ...otherProps }) => {
	const { format, locale } = useI18n();
	const translations = translationsManager.getLocaleWordings(locale);

	const [step, setStep] = useState<SurveyDialogStep>(() => SurveyDialogStep.SurveyInvitation);
	const [widthSize, setWidthSize] = useState(DIALOG_SIZE.S);
	const [heightSize, setHeightSize] = useState("");

	const isSurveyCompleted = useObservable(userSurveyManager.isSurveyCompleted);

	const popupTitle = useMemo(
		() =>
			survey.popupTitleKey in translations ? translations[survey.popupTitleKey] : format("survey.popup.nps.title"),
		[format, survey.popupTitleKey, translations],
	);

	const onClose = useCallback(async () => {
		if (!isSurveyCompleted) {
			await userSurveyManager.dismissSurvey(survey.id);

			analyticsService.trackEvent({
				category: AnalyticsCategory.SURVEY,
				action: "modaleSecondaryCta",
				params: [survey.title],
			});
		}

		onHide();
	}, [isSurveyCompleted, onHide, survey]);

	const renderStep = useCallback(() => {
		switch (step) {
			case SurveyDialogStep.SurveyInvitation:
				return (
					<InviteSurvey
						onClose={onClose}
						nextStepSurvey={() => {
							setStep(SurveyDialogStep.SurveyForm);
						}}
						surveyWordingKeys={{
							popupBodyKey: survey.popupBodyKey,
							popupPrimaryCtaKey: survey.popupPrimaryCtaKey,
							popupSecondaryCtaKey: survey.popupSecondaryCtaKey,
						}}
					/>
				);
			case SurveyDialogStep.SurveyForm:
				return <CreateSurveyForm survey={survey} setWidth={setWidthSize} setHeight={setHeightSize} />;
		}
	}, [survey, onClose, step]);

	return (
		<BaseDialog
			dismissableMask={isSurveyCompleted}
			closeOnEscape={false}
			visible={true}
			onHide={onClose}
			width={widthSize}
			height={heightSize}
			header={step === SurveyDialogStep.SurveyInvitation ? popupTitle : ""}
			hideScrollbar={true}
			{...otherProps}
		>
			<DialogContainer>{renderStep()}</DialogContainer>
		</BaseDialog>
	);
};

const DialogContainer = styled.div`
	${stack("M", "flexstart", "stretch")};
`;
